// ============================================
// CASE TEMPLATES
// Organized by category, with specialty relevance tags
// ============================================

export interface CaseTemplate {
  id: string;
  name: string;
  description: string;
  category: "Medication" | "Imaging" | "Procedure" | "DME" | "Referral";
  icon: string;
  formData: Record<string, string>;
  suggestedConditions: string[];
  suggestedTreatments: string[];
  // Specialties that commonly use this template (for future filtering)
  relevantSpecialties?: string[];
}

export const TEMPLATE_CATEGORIES = [
  { id: "Medication", label: "Medications", icon: "medication" },
  { id: "Imaging", label: "Imaging", icon: "imaging" },
  { id: "Procedure", label: "Procedures", icon: "procedure" },
  { id: "DME", label: "DME", icon: "dme" },
  { id: "Referral", label: "Referrals", icon: "referral" },
] as const;

export const CASE_TEMPLATES: CaseTemplate[] = [
  // ============================================
  // MEDICATIONS
  // ============================================

  // --- Diabetes / Endocrinology ---
  {
    id: "glp1_t2dm",
    name: "GLP-1 for Type 2 Diabetes",
    description: "Ozempic, Mounjaro, Wegovy for uncontrolled T2DM",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "GLP-1 Agonist",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["dm2", "obesity", "ascvd"],
    suggestedTreatments: ["metformin", "sulfonylurea", "diet_modification"],
    relevantSpecialties: [
      "endocrinology",
      "internal_medicine",
      "family_medicine",
    ],
  },
  {
    id: "glp1_obesity",
    name: "GLP-1 for Obesity",
    description: "Wegovy, Zepbound for weight management",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "GLP-1 Agonist (Weight Loss)",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["obesity", "osa", "htn", "dm2"],
    suggestedTreatments: ["diet_modification", "exercise_program", "orlistat"],
    relevantSpecialties: [
      "endocrinology",
      "internal_medicine",
      "family_medicine",
    ],
  },
  {
    id: "sglt2_hf",
    name: "SGLT2 Inhibitor for Heart Failure",
    description: "Jardiance, Farxiga for HFrEF",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "SGLT2 Inhibitor",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["hfref", "hfpef", "dm2", "ckd"],
    suggestedTreatments: ["ace_inhibitor", "arb", "beta_blocker", "diuretic"],
    relevantSpecialties: ["cardiology", "nephrology", "internal_medicine"],
  },
  {
    id: "insulin_pump",
    name: "Insulin Pump",
    description: "For intensive insulin management in T1DM/T2DM",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Diabetes Supplies",
      dmeSpecificItem: "Insulin Pump",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["dm1", "dm2", "brittle_diabetes"],
    suggestedTreatments: ["mdi", "basal_bolus", "cgm"],
    relevantSpecialties: ["endocrinology"],
  },

  // --- Cardiology ---
  {
    id: "pcsk9",
    name: "PCSK9 Inhibitor",
    description: "Repatha, Praluent for refractory hyperlipidemia",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "PCSK9 Inhibitor",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["hyperlipidemia", "ascvd", "fh"],
    suggestedTreatments: ["high_intensity_statin", "ezetimibe"],
    relevantSpecialties: ["cardiology", "internal_medicine"],
  },
  {
    id: "entresto",
    name: "Entresto for Heart Failure",
    description: "Sacubitril/valsartan for HFrEF",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Entresto",
      medicationClass: "ARNI",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["hfref"],
    suggestedTreatments: ["ace_inhibitor", "arb", "beta_blocker"],
    relevantSpecialties: ["cardiology"],
  },

  // --- Rheumatology ---
  {
    id: "biologic_ra",
    name: "Biologic for Rheumatoid Arthritis",
    description: "Humira, Enbrel, Rinvoq for RA",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["ra"],
    suggestedTreatments: [
      "methotrexate",
      "sulfasalazine",
      "hydroxychloroquine",
      "nsaids",
    ],
    relevantSpecialties: ["rheumatology"],
  },
  {
    id: "biologic_psoriasis",
    name: "Biologic for Psoriasis/PsA",
    description: "Skyrizi, Tremfya, Cosentyx for psoriatic disease",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["psoriasis", "psa"],
    suggestedTreatments: ["topical_steroids", "methotrexate", "phototherapy"],
    relevantSpecialties: ["rheumatology", "dermatology"],
  },
  {
    id: "biologic_as",
    name: "Biologic for Ankylosing Spondylitis",
    description: "TNF inhibitor or IL-17 inhibitor for AS",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["ankylosing_spondylitis"],
    suggestedTreatments: ["nsaids", "physical_therapy", "sulfasalazine"],
    relevantSpecialties: ["rheumatology"],
  },
  {
    id: "jak_inhibitor",
    name: "JAK Inhibitor",
    description: "Rinvoq, Xeljanz for RA, UC, or atopic dermatitis",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "JAK Inhibitor",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["ra", "uc", "atopic_dermatitis"],
    suggestedTreatments: ["biologic", "methotrexate", "topical_steroids"],
    relevantSpecialties: ["rheumatology", "gastroenterology", "dermatology"],
  },

  // --- Gastroenterology ---
  {
    id: "biologic_ibd",
    name: "Biologic for IBD",
    description: "Humira, Stelara, Entyvio for Crohn's or UC",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["crohns", "uc", "ibd"],
    suggestedTreatments: ["mesalamine", "steroids", "azathioprine", "6mp"],
    relevantSpecialties: ["gastroenterology"],
  },

  // --- Neurology ---
  {
    id: "ms_medication",
    name: "MS Disease-Modifying Therapy",
    description: "Ocrevus, Kesimpta, Tysabri for multiple sclerosis",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "MS Disease-Modifying Therapy",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["ms"],
    suggestedTreatments: ["interferon", "glatiramer", "dimethyl_fumarate"],
    relevantSpecialties: ["neurology"],
  },
  {
    id: "migraine_cgrp",
    name: "CGRP Inhibitor for Migraine",
    description: "Aimovig, Ajovy, Emgality, Nurtec for chronic migraine",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "CGRP Inhibitor",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["chronic_migraine", "migraine"],
    suggestedTreatments: [
      "topiramate",
      "propranolol",
      "amitriptyline",
      "botox",
    ],
    relevantSpecialties: ["neurology", "pain_management"],
  },
  {
    id: "botox_migraine",
    name: "Botox for Chronic Migraine",
    description: "OnabotulinumtoxinA for chronic migraine prophylaxis",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Procedure",
      procedureName: "Botox Injection for Migraine",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["chronic_migraine"],
    suggestedTreatments: [
      "topiramate",
      "propranolol",
      "amitriptyline",
      "cgrp_inhibitor",
    ],
    relevantSpecialties: ["neurology", "pain_management"],
  },

  // --- Pulmonology / Allergy ---
  {
    id: "biologic_asthma",
    name: "Biologic for Severe Asthma",
    description: "Dupixent, Nucala, Fasenra for uncontrolled asthma",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["severe_asthma", "eosinophilic_asthma"],
    suggestedTreatments: ["ics_laba", "lama", "oral_steroids", "ltra"],
    relevantSpecialties: ["pulmonology", "allergy_immunology"],
  },
  {
    id: "biologic_eczema",
    name: "Biologic for Atopic Dermatitis",
    description: "Dupixent for moderate-to-severe eczema",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Biologic / Immunomodulator",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["atopic_dermatitis", "eczema"],
    suggestedTreatments: [
      "topical_steroids",
      "topical_calcineurin",
      "phototherapy",
    ],
    relevantSpecialties: ["dermatology", "allergy_immunology"],
  },

  // --- Psychiatry ---
  {
    id: "ketamine_esketamine",
    name: "Esketamine (Spravato)",
    description: "For treatment-resistant depression",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Spravato (esketamine)",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["trd", "mdd"],
    suggestedTreatments: ["ssri", "snri", "tca", "maoi", "augmentation"],
    relevantSpecialties: ["psychiatry"],
  },

  // --- Oncology ---
  {
    id: "immunotherapy",
    name: "Immunotherapy",
    description: "Keytruda, Opdivo for cancer treatment",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationClass: "Immunotherapy",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["cancer", "melanoma", "nsclc", "rcc"],
    suggestedTreatments: ["chemotherapy", "radiation", "surgery"],
    relevantSpecialties: ["oncology"],
  },

  // --- OB/GYN Medications ---
  {
    id: "iud",
    name: "IUD (Mirena, Kyleena, Liletta)",
    description: "Hormonal IUD for contraception or heavy bleeding",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Procedure",
      procedureName: "IUD Insertion",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: [
      "contraception",
      "menorrhagia",
      "dysmenorrhea",
      "endometriosis",
    ],
    suggestedTreatments: ["oral_contraceptives", "depo_provera"],
    relevantSpecialties: ["obgyn", "family_medicine"],
  },
  {
    id: "nexplanon",
    name: "Nexplanon (Contraceptive Implant)",
    description: "Etonogestrel subdermal implant",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Procedure",
      procedureName: "Nexplanon Insertion",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: ["contraception"],
    suggestedTreatments: ["oral_contraceptives", "iud", "depo_provera"],
    relevantSpecialties: ["obgyn", "family_medicine"],
  },
  {
    id: "paragard",
    name: "Paragard (Copper IUD)",
    description: "Non-hormonal IUD for contraception",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Procedure",
      procedureName: "Paragard IUD Insertion",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: ["contraception", "hormone_contraindication"],
    suggestedTreatments: ["barrier_methods"],
    relevantSpecialties: ["obgyn", "family_medicine"],
  },
  {
    id: "makena",
    name: "Makena / 17P (Progesterone)",
    description: "For preterm birth prevention in high-risk pregnancy",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Makena (17-alpha hydroxyprogesterone)",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "history_preterm_birth",
      "short_cervix",
      "high_risk_pregnancy",
    ],
    suggestedTreatments: ["cerclage", "progesterone_suppository"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },
  {
    id: "orilissa",
    name: "Orilissa (Elagolix)",
    description: "For endometriosis-associated pain",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Orilissa (elagolix)",
      denialReason: "Step therapy required",
    },
    suggestedConditions: ["endometriosis", "dysmenorrhea", "dyspareunia"],
    suggestedTreatments: [
      "nsaids",
      "oral_contraceptives",
      "progestins",
      "gnrh_agonist",
    ],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "lupron_gyn",
    name: "Lupron for Fibroids/Endometriosis",
    description: "GnRH agonist for fibroids or endometriosis",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Lupron Depot",
      medicationClass: "GnRH Agonist",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["fibroids", "endometriosis", "preoperative_fibroid"],
    suggestedTreatments: ["oral_contraceptives", "progestins", "nsaids"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "letrozole_fertility",
    name: "Letrozole for Ovulation Induction",
    description: "First-line for PCOS-related infertility",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Letrozole (Femara)",
      denialReason: "Off-label use",
    },
    suggestedConditions: ["pcos", "anovulation", "infertility"],
    suggestedTreatments: ["lifestyle_modification", "metformin"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },
  {
    id: "progesterone_support",
    name: "Progesterone (Luteal Support)",
    description: "Prometrium, Crinone, Endometrin for pregnancy support",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Progesterone",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "luteal_phase_defect",
      "ivf",
      "recurrent_loss",
      "threatened_miscarriage",
    ],
    suggestedTreatments: [],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },
  {
    id: "clomid",
    name: "Clomid (Clomiphene)",
    description: "For ovulation induction",
    category: "Medication",
    icon: "medication",
    formData: {
      requestType: "Medication",
      medicationName: "Clomid (clomiphene citrate)",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: ["anovulation", "infertility", "pcos"],
    suggestedTreatments: ["lifestyle_modification"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },

  // ============================================
  // IMAGING
  // ============================================

  // --- Spine ---
  {
    id: "mri_lumbar",
    name: "MRI Lumbar Spine",
    description: "For radiculopathy, stenosis, or disc herniation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Lumbar spine",
    },
    suggestedConditions: [
      "radiculopathy",
      "spinal_stenosis",
      "disc_herniation",
      "lbp",
    ],
    suggestedTreatments: ["physical_therapy", "nsaids", "epidural_injection"],
    relevantSpecialties: [
      "orthopedics",
      "neurosurgery",
      "pain_management",
      "pm_r",
    ],
  },
  {
    id: "mri_cervical",
    name: "MRI Cervical Spine",
    description: "For neck pain with radiculopathy or myelopathy",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Cervical spine",
    },
    suggestedConditions: ["cervical_radiculopathy", "myelopathy", "neck_pain"],
    suggestedTreatments: ["physical_therapy", "nsaids", "cervical_injection"],
    relevantSpecialties: ["orthopedics", "neurosurgery", "pain_management"],
  },
  {
    id: "mri_thoracic",
    name: "MRI Thoracic Spine",
    description: "For thoracic pain, myelopathy, or tumor evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Thoracic spine",
    },
    suggestedConditions: ["thoracic_pain", "myelopathy", "spinal_tumor"],
    suggestedTreatments: ["physical_therapy", "nsaids"],
    relevantSpecialties: ["orthopedics", "neurosurgery", "oncology"],
  },

  // --- Joints ---
  {
    id: "mri_knee",
    name: "MRI Knee",
    description: "For meniscal tear, ACL injury, or unexplained pain",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Knee",
    },
    suggestedConditions: ["meniscal_tear", "acl_tear", "knee_pain", "oa_knee"],
    suggestedTreatments: ["physical_therapy", "nsaids", "xray"],
    relevantSpecialties: ["orthopedics", "sports_medicine"],
  },
  {
    id: "mri_shoulder",
    name: "MRI Shoulder",
    description: "For rotator cuff tear, labral tear, or impingement",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Shoulder",
    },
    suggestedConditions: [
      "rotator_cuff_tear",
      "labral_tear",
      "impingement",
      "shoulder_pain",
    ],
    suggestedTreatments: [
      "physical_therapy",
      "nsaids",
      "xray",
      "steroid_injection",
    ],
    relevantSpecialties: ["orthopedics", "sports_medicine"],
  },
  {
    id: "mri_hip",
    name: "MRI Hip",
    description: "For labral tear, AVN, or occult fracture",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Hip",
    },
    suggestedConditions: ["labral_tear", "avn", "hip_pain", "fai"],
    suggestedTreatments: ["physical_therapy", "nsaids", "xray"],
    relevantSpecialties: ["orthopedics", "sports_medicine"],
  },

  // --- Neuro ---
  {
    id: "mri_brain",
    name: "MRI Brain",
    description: "For headache, seizure, MS, or tumor evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Brain",
    },
    suggestedConditions: ["headache", "seizure", "ms", "brain_tumor", "stroke"],
    suggestedTreatments: ["ct_head", "neurology_consult"],
    relevantSpecialties: ["neurology", "neurosurgery"],
  },

  // --- Cardiac ---
  {
    id: "cardiac_mri",
    name: "Cardiac MRI",
    description: "For cardiomyopathy, myocarditis, or viability assessment",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Heart",
    },
    suggestedConditions: [
      "cardiomyopathy",
      "myocarditis",
      "hfref",
      "infiltrative_cm",
    ],
    suggestedTreatments: ["echocardiogram", "stress_test"],
    relevantSpecialties: ["cardiology"],
  },
  {
    id: "stress_test",
    name: "Stress Test",
    description: "Nuclear or echo stress for CAD evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "Stress Test (Nuclear/Echo)",
      bodyRegion: "Heart",
    },
    suggestedConditions: ["chest_pain", "cad", "dyspnea", "preop_cardiac"],
    suggestedTreatments: ["ekg", "echocardiogram"],
    relevantSpecialties: ["cardiology", "internal_medicine"],
  },
  {
    id: "echo",
    name: "Echocardiogram",
    description: "For heart failure, murmur, or valve evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "Echocardiogram",
      bodyRegion: "Heart",
    },
    suggestedConditions: ["hf", "murmur", "valve_disease", "dyspnea"],
    suggestedTreatments: ["ekg", "bnp"],
    relevantSpecialties: ["cardiology"],
  },
  {
    id: "cta_coronary",
    name: "CT Coronary Angiography",
    description: "Non-invasive CAD evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "CT",
      bodyRegion: "Coronary arteries",
      contrastRequired: "Yes",
    },
    suggestedConditions: ["chest_pain", "cad", "intermediate_risk"],
    suggestedTreatments: ["stress_test", "ekg"],
    relevantSpecialties: ["cardiology"],
  },

  // --- Chest/Pulm ---
  {
    id: "ct_chest",
    name: "CT Chest",
    description: "For pulmonary nodule, ILD, or cancer staging",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "CT",
      bodyRegion: "Chest",
    },
    suggestedConditions: ["lung_nodule", "ild", "lung_cancer", "copd"],
    suggestedTreatments: ["chest_xray", "pft"],
    relevantSpecialties: ["pulmonology", "oncology"],
  },

  // --- Abdomen/Pelvis ---
  {
    id: "ct_abdomen",
    name: "CT Abdomen/Pelvis",
    description: "For abdominal pain, mass, or cancer staging",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "CT",
      bodyRegion: "Abdomen/Pelvis",
    },
    suggestedConditions: [
      "abdominal_pain",
      "abdominal_mass",
      "cancer",
      "diverticulitis",
    ],
    suggestedTreatments: ["ultrasound", "labs"],
    relevantSpecialties: [
      "gastroenterology",
      "general_surgery",
      "oncology",
      "urology",
    ],
  },
  {
    id: "mrcp",
    name: "MRCP",
    description: "For biliary/pancreatic duct evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Biliary/Pancreatic ducts (MRCP)",
    },
    suggestedConditions: [
      "choledocholithiasis",
      "psc",
      "pancreatic_mass",
      "biliary_stricture",
    ],
    suggestedTreatments: ["ultrasound", "ct_abdomen"],
    relevantSpecialties: ["gastroenterology"],
  },

  // --- OB/GYN ---
  {
    id: "pelvic_us",
    name: "Pelvic Ultrasound",
    description: "For fibroids, ovarian cysts, abnormal bleeding",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "Ultrasound",
      bodyRegion: "Pelvis",
    },
    suggestedConditions: [
      "fibroids",
      "ovarian_cyst",
      "abnormal_bleeding",
      "pelvic_pain",
    ],
    suggestedTreatments: [],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "mri_pelvis",
    name: "MRI Pelvis",
    description: "For endometriosis, fibroids staging, cancer evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "MRI",
      bodyRegion: "Pelvis",
    },
    suggestedConditions: [
      "endometriosis",
      "fibroids",
      "cervical_cancer",
      "pelvic_mass",
    ],
    suggestedTreatments: ["pelvic_ultrasound", "ct_pelvis"],
    relevantSpecialties: ["obgyn", "oncology"],
  },
  {
    id: "hsg",
    name: "HSG (Hysterosalpingogram)",
    description: "For tubal patency evaluation in infertility",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "Fluoroscopy",
      bodyRegion: "Uterus/Fallopian tubes",
    },
    suggestedConditions: ["infertility", "tubal_factor"],
    suggestedTreatments: ["semen_analysis", "ovulation_testing"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },

  // --- Sleep ---
  {
    id: "sleep_study",
    name: "Sleep Study (PSG)",
    description: "For obstructive sleep apnea evaluation",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Procedure",
      procedureName: "Polysomnography (Sleep Study)",
    },
    suggestedConditions: [
      "osa",
      "sleep_disorder",
      "excessive_sleepiness",
      "witnessed_apnea",
    ],
    suggestedTreatments: ["home_sleep_test", "epworth_scale"],
    relevantSpecialties: ["sleep_medicine", "pulmonology", "ent"],
  },

  // --- Oncology ---
  {
    id: "pet_scan",
    name: "PET/CT Scan",
    description: "For cancer staging or treatment response",
    category: "Imaging",
    icon: "imaging",
    formData: {
      requestType: "Imaging",
      imagingType: "PET/CT",
      bodyRegion: "Whole body",
    },
    suggestedConditions: ["cancer", "lymphoma", "lung_cancer", "melanoma"],
    suggestedTreatments: ["ct_scan", "biopsy"],
    relevantSpecialties: ["oncology"],
  },

  // ============================================
  // PROCEDURES
  // ============================================

  // --- Orthopedics ---
  {
    id: "tka",
    name: "Total Knee Replacement",
    description: "TKA for end-stage osteoarthritis",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Total Knee Arthroplasty",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["oa_knee", "knee_pain"],
    suggestedTreatments: [
      "physical_therapy",
      "nsaids",
      "steroid_injection",
      "hyaluronic_acid",
    ],
    relevantSpecialties: ["orthopedics"],
  },
  {
    id: "tha",
    name: "Total Hip Replacement",
    description: "THA for end-stage osteoarthritis or AVN",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Total Hip Arthroplasty",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["oa_hip", "avn", "hip_pain"],
    suggestedTreatments: ["physical_therapy", "nsaids", "steroid_injection"],
    relevantSpecialties: ["orthopedics"],
  },
  {
    id: "lumbar_fusion",
    name: "Lumbar Spinal Fusion",
    description: "For spondylolisthesis, instability, or DDD",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Lumbar Spinal Fusion",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "spondylolisthesis",
      "spinal_instability",
      "ddd",
      "spinal_stenosis",
    ],
    suggestedTreatments: [
      "physical_therapy",
      "epidural_injection",
      "facet_injection",
      "rfa",
    ],
    relevantSpecialties: ["orthopedics", "neurosurgery"],
  },
  {
    id: "arthroscopy_knee",
    name: "Knee Arthroscopy",
    description: "For meniscal repair or debridement",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Knee Arthroscopy",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["meniscal_tear", "loose_body", "knee_pain"],
    suggestedTreatments: ["physical_therapy", "nsaids", "mri"],
    relevantSpecialties: ["orthopedics", "sports_medicine"],
  },
  {
    id: "rotator_cuff_repair",
    name: "Rotator Cuff Repair",
    description: "For rotator cuff tear",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Rotator Cuff Repair",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["rotator_cuff_tear", "shoulder_pain"],
    suggestedTreatments: [
      "physical_therapy",
      "nsaids",
      "steroid_injection",
      "mri",
    ],
    relevantSpecialties: ["orthopedics", "sports_medicine"],
  },

  // --- Pain Management ---
  {
    id: "epidural_injection",
    name: "Epidural Steroid Injection",
    description: "For radiculopathy or spinal stenosis",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Epidural Steroid Injection",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "radiculopathy",
      "spinal_stenosis",
      "disc_herniation",
    ],
    suggestedTreatments: ["physical_therapy", "nsaids", "oral_steroids", "mri"],
    relevantSpecialties: ["pain_management", "pm_r"],
  },
  {
    id: "spinal_cord_stim",
    name: "Spinal Cord Stimulator",
    description: "For chronic intractable pain",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Spinal Cord Stimulator Implantation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["fbss", "crps", "chronic_pain"],
    suggestedTreatments: [
      "physical_therapy",
      "medications",
      "injections",
      "scs_trial",
    ],
    relevantSpecialties: ["pain_management"],
  },
  {
    id: "rfa",
    name: "Radiofrequency Ablation",
    description: "For facet or SI joint pain",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Radiofrequency Ablation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["facet_arthropathy", "si_joint_pain", "chronic_lbp"],
    suggestedTreatments: [
      "physical_therapy",
      "medial_branch_block",
      "si_joint_injection",
    ],
    relevantSpecialties: ["pain_management", "pm_r"],
  },

  // --- Cardiology ---
  {
    id: "cardiac_cath",
    name: "Cardiac Catheterization",
    description: "For CAD evaluation or intervention",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Cardiac Catheterization",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["cad", "acs", "positive_stress", "chest_pain"],
    suggestedTreatments: ["stress_test", "cta_coronary", "medical_therapy"],
    relevantSpecialties: ["cardiology"],
  },
  {
    id: "pacemaker",
    name: "Pacemaker Implantation",
    description: "For bradycardia or heart block",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Pacemaker Implantation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["bradycardia", "heart_block", "sss", "syncope"],
    suggestedTreatments: ["ekg", "holter", "event_monitor"],
    relevantSpecialties: ["cardiology"],
  },
  {
    id: "icd",
    name: "ICD Implantation",
    description: "For sudden cardiac death prevention",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "ICD Implantation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["hfref", "vt", "vf", "scd_prevention"],
    suggestedTreatments: ["echocardiogram", "gdmt", "electrophysiology_study"],
    relevantSpecialties: ["cardiology"],
  },

  // --- GI ---
  {
    id: "colonoscopy",
    name: "Colonoscopy",
    description: "For screening, surveillance, or symptoms",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Colonoscopy",
    },
    suggestedConditions: [
      "crc_screening",
      "gi_bleeding",
      "change_bowel_habits",
      "polyp_surveillance",
    ],
    suggestedTreatments: ["fit_test", "cologuard"],
    relevantSpecialties: ["gastroenterology", "general_surgery"],
  },
  {
    id: "egd",
    name: "EGD (Upper Endoscopy)",
    description: "For GERD, dysphagia, or GI bleeding",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Esophagogastroduodenoscopy (EGD)",
    },
    suggestedConditions: ["gerd", "dysphagia", "upper_gi_bleeding", "barretts"],
    suggestedTreatments: ["ppi_trial", "h_pylori_testing"],
    relevantSpecialties: ["gastroenterology"],
  },
  {
    id: "ercp",
    name: "ERCP",
    description: "For bile duct stones or strictures",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "ERCP",
    },
    suggestedConditions: [
      "choledocholithiasis",
      "biliary_stricture",
      "cholangitis",
    ],
    suggestedTreatments: ["mrcp", "eus"],
    relevantSpecialties: ["gastroenterology"],
  },

  // --- OB/GYN ---
  {
    id: "hysterectomy",
    name: "Hysterectomy",
    description: "For fibroids, abnormal bleeding, or endometriosis",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Hysterectomy",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "fibroids",
      "abnormal_bleeding",
      "endometriosis",
      "adenomyosis",
    ],
    suggestedTreatments: [
      "hormonal_therapy",
      "iud",
      "endometrial_ablation",
      "myomectomy",
    ],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "myomectomy",
    name: "Myomectomy",
    description: "Fibroid removal preserving fertility",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Myomectomy",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["fibroids", "infertility", "abnormal_bleeding"],
    suggestedTreatments: ["hormonal_therapy", "gnrh_agonist"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "endometrial_ablation",
    name: "Endometrial Ablation",
    description: "For heavy menstrual bleeding",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Endometrial Ablation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["menorrhagia", "abnormal_bleeding"],
    suggestedTreatments: ["hormonal_therapy", "iud", "nsaids"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "fertility_ivf",
    name: "IVF Treatment",
    description: "In vitro fertilization for infertility",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "In Vitro Fertilization (IVF)",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: [
      "infertility",
      "tubal_factor",
      "male_factor",
      "unexplained_infertility",
    ],
    suggestedTreatments: ["clomid", "iui", "letrozole"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },
  {
    id: "colposcopy",
    name: "Colposcopy with Biopsy",
    description: "For abnormal Pap smear or cervical dysplasia",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Colposcopy with Biopsy",
    },
    suggestedConditions: [
      "abnormal_pap",
      "hpv_positive",
      "cervical_dysplasia",
      "cin",
    ],
    suggestedTreatments: ["pap_smear", "hpv_testing"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "leep",
    name: "LEEP Procedure",
    description: "Loop excision for cervical dysplasia",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "LEEP (Loop Electrosurgical Excision Procedure)",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["cin2", "cin3", "cervical_dysplasia", "hsil"],
    suggestedTreatments: ["colposcopy", "pap_smear"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "dnc",
    name: "D&C (Dilation and Curettage)",
    description: "For abnormal bleeding, miscarriage, or endometrial sampling",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Dilation and Curettage (D&C)",
    },
    suggestedConditions: [
      "abnormal_bleeding",
      "miscarriage",
      "endometrial_hyperplasia",
      "postmenopausal_bleeding",
    ],
    suggestedTreatments: ["ultrasound", "endometrial_biopsy"],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "laparoscopy_endo",
    name: "Laparoscopy for Endometriosis",
    description: "Diagnostic and therapeutic laparoscopy for endometriosis",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Laparoscopy for Endometriosis",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "endometriosis",
      "chronic_pelvic_pain",
      "infertility",
      "dysmenorrhea",
    ],
    suggestedTreatments: [
      "nsaids",
      "oral_contraceptives",
      "orilissa",
      "lupron",
      "mri_pelvis",
    ],
    relevantSpecialties: ["obgyn"],
  },
  {
    id: "iui",
    name: "Intrauterine Insemination (IUI)",
    description: "For unexplained or mild male factor infertility",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Intrauterine Insemination (IUI)",
      denialReason: "Not covered benefit",
    },
    suggestedConditions: [
      "infertility",
      "unexplained_infertility",
      "mild_male_factor",
    ],
    suggestedTreatments: ["clomid", "letrozole", "semen_analysis"],
    relevantSpecialties: ["obgyn", "maternal_fetal"],
  },

  // --- General Surgery ---
  {
    id: "bariatric",
    name: "Bariatric Surgery",
    description: "Gastric bypass or sleeve for morbid obesity",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Bariatric Surgery",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["morbid_obesity", "obesity", "dm2", "osa"],
    suggestedTreatments: ["diet_program", "glp1", "exercise_program"],
    relevantSpecialties: ["general_surgery"],
  },
  {
    id: "cholecystectomy",
    name: "Cholecystectomy",
    description: "Gallbladder removal for stones or cholecystitis",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Laparoscopic Cholecystectomy",
    },
    suggestedConditions: ["cholelithiasis", "cholecystitis", "biliary_colic"],
    suggestedTreatments: ["ultrasound", "hida_scan"],
    relevantSpecialties: ["general_surgery"],
  },

  // --- ENT ---
  {
    id: "tonsillectomy",
    name: "Tonsillectomy",
    description: "For recurrent tonsillitis or OSA",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Tonsillectomy",
    },
    suggestedConditions: [
      "recurrent_tonsillitis",
      "osa",
      "tonsillar_hypertrophy",
    ],
    suggestedTreatments: ["antibiotics", "sleep_study"],
    relevantSpecialties: ["ent"],
  },
  {
    id: "sinus_surgery",
    name: "Sinus Surgery (FESS)",
    description: "For chronic sinusitis refractory to medical therapy",
    category: "Procedure",
    icon: "procedure",
    formData: {
      requestType: "Procedure",
      procedureName: "Functional Endoscopic Sinus Surgery (FESS)",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: ["chronic_sinusitis", "nasal_polyps"],
    suggestedTreatments: ["nasal_steroids", "antibiotics", "ct_sinus"],
    relevantSpecialties: ["ent"],
  },

  // ============================================
  // DME
  // ============================================
  {
    id: "cpap",
    name: "CPAP Machine",
    description: "For obstructive sleep apnea",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Respiratory",
      dmeSpecificItem: "CPAP Machine",
    },
    suggestedConditions: ["osa"],
    suggestedTreatments: ["sleep_study"],
    relevantSpecialties: ["sleep_medicine", "pulmonology"],
  },
  {
    id: "bipap",
    name: "BiPAP Machine",
    description: "For OSA with high pressures or hypoventilation",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Respiratory",
      dmeSpecificItem: "BiPAP Machine",
    },
    suggestedConditions: ["osa", "ohs", "copd", "neuromuscular_disease"],
    suggestedTreatments: ["cpap_failure", "sleep_study"],
    relevantSpecialties: ["sleep_medicine", "pulmonology"],
  },
  {
    id: "home_oxygen",
    name: "Home Oxygen",
    description: "For chronic hypoxemia",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Respiratory",
      dmeSpecificItem: "Home Oxygen Therapy",
    },
    suggestedConditions: ["copd", "ild", "pulm_htn", "hypoxemia"],
    suggestedTreatments: ["abg", "pulse_ox", "six_minute_walk"],
    relevantSpecialties: ["pulmonology"],
  },
  {
    id: "cgm",
    name: "Continuous Glucose Monitor",
    description: "Dexcom, Libre for diabetes management",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Diabetes Supplies",
      dmeSpecificItem: "Continuous Glucose Monitor",
    },
    suggestedConditions: ["dm1", "dm2", "hypoglycemia_unawareness"],
    suggestedTreatments: ["insulin", "fingerstick_monitoring"],
    relevantSpecialties: ["endocrinology"],
  },
  {
    id: "wheelchair",
    name: "Power Wheelchair",
    description: "For mobility impairment",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Mobility",
      dmeSpecificItem: "Power Wheelchair",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "mobility_impairment",
      "ms",
      "als",
      "spinal_cord_injury",
    ],
    suggestedTreatments: ["manual_wheelchair", "pt_evaluation"],
    relevantSpecialties: ["pm_r", "neurology"],
  },
  {
    id: "hospital_bed",
    name: "Hospital Bed",
    description: "For positioning needs or wound care",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Mobility",
      dmeSpecificItem: "Hospital Bed",
    },
    suggestedConditions: [
      "bedbound",
      "wound_care",
      "respiratory_failure",
      "chf",
    ],
    suggestedTreatments: [],
    relevantSpecialties: ["hospice_palliative", "wound_care"],
  },
  {
    id: "wound_vac",
    name: "Wound VAC",
    description: "Negative pressure wound therapy",
    category: "DME",
    icon: "dme",
    formData: {
      requestType: "DME (Durable Medical Equipment)",
      dmeCategory: "Wound Care",
      dmeSpecificItem: "Negative Pressure Wound Therapy",
    },
    suggestedConditions: ["chronic_wound", "surgical_wound", "diabetic_ulcer"],
    suggestedTreatments: ["wound_care", "debridement"],
    relevantSpecialties: ["wound_care", "general_surgery"],
  },

  // ============================================
  // REFERRALS
  // ============================================
  {
    id: "physical_therapy",
    name: "Physical Therapy",
    description: "For MSK conditions or post-surgical rehab",
    category: "Referral",
    icon: "referral",
    formData: {
      requestType: "Referral",
      referralSpecialty: "Physical Therapy",
    },
    suggestedConditions: ["lbp", "knee_pain", "shoulder_pain", "post_surgical"],
    suggestedTreatments: ["nsaids", "home_exercises"],
    relevantSpecialties: ["orthopedics", "pm_r", "pain_management"],
  },
  {
    id: "inpatient_rehab",
    name: "Inpatient Rehabilitation",
    description: "For stroke, TBI, or major surgery recovery",
    category: "Referral",
    icon: "referral",
    formData: {
      requestType: "Referral",
      referralSpecialty: "Inpatient Rehabilitation",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "stroke",
      "tbi",
      "spinal_cord_injury",
      "major_surgery",
    ],
    suggestedTreatments: ["acute_hospitalization"],
    relevantSpecialties: ["pm_r", "neurology"],
  },
  {
    id: "snf",
    name: "Skilled Nursing Facility",
    description: "For post-acute care and rehabilitation",
    category: "Referral",
    icon: "referral",
    formData: {
      requestType: "Referral",
      referralSpecialty: "Skilled Nursing Facility",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "post_surgical",
      "wound_care",
      "iv_antibiotics",
      "debility",
    ],
    suggestedTreatments: ["acute_hospitalization"],
    relevantSpecialties: ["geriatrics", "hospitalist"],
  },
  {
    id: "home_health",
    name: "Home Health Services",
    description: "Skilled nursing and therapy at home",
    category: "Referral",
    icon: "referral",
    formData: {
      requestType: "Referral",
      referralSpecialty: "Home Health",
    },
    suggestedConditions: ["homebound", "wound_care", "iv_therapy", "chf"],
    suggestedTreatments: [],
    relevantSpecialties: ["geriatrics", "internal_medicine"],
  },
  {
    id: "genetic_testing",
    name: "Genetic Testing",
    description: "For hereditary conditions or cancer risk",
    category: "Referral",
    icon: "referral",
    formData: {
      requestType: "Procedure",
      procedureName: "Genetic Testing",
      denialReason: "Not medically necessary",
    },
    suggestedConditions: [
      "family_hx_cancer",
      "brca",
      "lynch_syndrome",
      "hereditary_condition",
    ],
    suggestedTreatments: ["genetic_counseling"],
    relevantSpecialties: ["oncology", "obgyn", "maternal_fetal"],
  },
];

// ============================================
// HELPER FUNCTION
// ============================================

export function getTemplateById(id: string): CaseTemplate | undefined {
  return CASE_TEMPLATES.find((t) => t.id === id);
}

export function getTemplatesBySpecialty(specialtyId: string): CaseTemplate[] {
  return CASE_TEMPLATES.filter((t) =>
    t.relevantSpecialties?.includes(specialtyId)
  );
}
